import { useState, useRef, useEffect } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, Zap, Facebook, Instagram, ChevronDown, ArrowRight } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

// Mega menu for About — with imagery
const ABOUT_ITEMS = [
  { to: "/about", label: "About Us", description: "Mission, Vision, Our Leaders", image: "/images/pastors-laughing.jpg", topAlign: true },
  { to: "/ministries", label: "Ministries", description: "16 Groups & Auxiliaries", image: "/images/ministries-worship.jpg" },
  { to: "/blog", label: "Blog & News", description: "Devotionals · Stories · Updates", image: "/images/blog-pastor.jpg" },
  { to: "/connect", label: "Connect With Us", description: "Social · Recent Posts · Follow", image: "/images/connect-pastor.jpg" },
];

// Mega menu for Member Services — with imagery
const MEMBER_SERVICES_ITEMS = [
  { to: "/events", label: "Events", description: "Calendar & RSVP", image: "/images/worship-service.jpg" },
  { to: "/sermons", label: "Sermons", description: "Watch & Listen", image: "/images/sermons-mic.jpg" },
  { to: "/new-members", label: "Next Steps", description: "First-Time Visitor Form", image: "/images/congregation.jpg" },
  { to: "/booking", label: "Book A Space", description: "Reserve A Facility", image: "/images/facility-1.jpg" },
];

const ABOUT_MATCH = ["/about", "/ministries", "/blog", "/connect"];
const MEMBER_MATCH = ["/events", "/sermons", "/new-members", "/booking", "/login", "/signup"];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileExpanded, setMobileExpanded] = useState({});
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const closeTimer = useRef(null);

  const hideOn = ["/portal", "/signup", "/login", "/verify"];
  const hidden =
    (location.pathname.startsWith("/admin") && location.pathname !== "/admin/login") ||
    hideOn.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"));

  useEffect(() => {
    setOpen(false);
    setActiveDropdown(null);
  }, [location.pathname]);

  if (hidden) return null;

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleEnter = (key) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setActiveDropdown(key);
  };
  const handleLeave = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setActiveDropdown(null), 140);
  };

  const matchesAny = (paths) =>
    paths.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"));

  return (
    <header className="sticky top-0 z-50 glass-dark" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 h-20 flex items-center justify-between gap-6">
        {/* LOGO */}
        <Link to="/" className="flex items-center gap-3 group flex-shrink-0" data-testid="nav-logo-link">
          <div className="relative">
            <img
              src="/logo.png"
              alt="The Push Center"
              className="w-11 h-11 rounded-full ring-1 ring-electric/40 group-hover:ring-electric transition-all"
            />
            <span className="absolute -inset-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity glow-electric pointer-events-none" />
          </div>
          <div className="hidden sm:flex flex-col leading-none">
            <span className="font-display font-black text-white text-lg tracking-tight">THE PUSH CENTER</span>
            <span className="text-[9px] uppercase tracking-[0.22em] text-electric mt-1" data-testid="nav-tagline">
              Out of Complacency Into Your Destiny.
            </span>
          </div>
        </Link>

        {/* DESKTOP NAV */}
        <nav className="hidden lg:flex items-center gap-1 flex-1 justify-center" aria-label="Primary">
          <NavLink
            to="/"
            end
            data-testid="nav-link-home"
            className={({ isActive }) =>
              `px-4 py-2 text-sm font-medium tracking-wide rounded-md transition-colors ${
                isActive ? "text-electric" : "text-zinc-300 hover:text-white"
              }`
            }
          >
            Home
          </NavLink>

          {/* ABOUT — MEGA MENU */}
          <div
            className="relative"
            onMouseEnter={() => handleEnter("about")}
            onMouseLeave={handleLeave}
          >
            <button
              type="button"
              onClick={() => setActiveDropdown(activeDropdown === "about" ? null : "about")}
              aria-expanded={activeDropdown === "about"}
              aria-haspopup="true"
              data-testid="nav-dropdown-about"
              className={`inline-flex items-center gap-1 px-4 py-2 text-sm font-medium tracking-wide rounded-md transition-colors whitespace-nowrap ${
                matchesAny(ABOUT_MATCH) || activeDropdown === "about" ? "text-electric" : "text-zinc-300 hover:text-white"
              }`}
            >
              About
              <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${activeDropdown === "about" ? "rotate-180" : ""}`} aria-hidden="true" />
            </button>
            {activeDropdown === "about" && (
              <div
                role="menu"
                data-testid="nav-dropdown-panel-about"
                className="absolute left-1/2 -translate-x-1/2 top-full pt-3"
                onMouseEnter={() => handleEnter("about")}
                onMouseLeave={handleLeave}
              >
                <div className="bg-[#050505] border border-white/10 rounded-3xl shadow-2xl shadow-black/80 overflow-hidden ring-1 ring-electric/15 w-[760px]">
                  <div className="grid grid-cols-3 gap-0">
                    {/* Left — 4 about tiles in 2x2 */}
                    <div className="col-span-2 p-4 grid grid-cols-2 gap-2 border-r border-white/5">
                      {ABOUT_ITEMS.map((sub) => {
                        const subActive = location.pathname === sub.to || location.pathname.startsWith(sub.to + "/");
                        return (
                          <Link
                            key={sub.to}
                            to={sub.to}
                            role="menuitem"
                            data-testid={`nav-dropdown-about-${sub.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className={`group relative overflow-hidden rounded-2xl border transition-all ${
                              subActive ? "border-electric bg-electric/5" : "border-white/5 hover:border-electric/40 bg-[#0a0a0a]"
                            }`}
                          >
                            <div className="relative h-28 overflow-hidden">
                              <img
                                src={sub.image}
                                alt=""
                                aria-hidden="true"
                                className={`absolute inset-0 w-full h-full object-cover ${sub.topAlign ? "object-top" : ""} opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-all duration-500`}
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-[#080808] via-[#080808]/40 to-transparent" />
                            </div>
                            <div className="relative -mt-6 p-4 pt-0">
                              <div className={`text-sm font-display font-bold tracking-tight mb-0.5 ${subActive ? "text-electric" : "text-white group-hover:text-electric"} transition-colors`}>
                                {sub.label}
                              </div>
                              <div className="text-[11px] text-zinc-500 leading-snug">{sub.description}</div>
                            </div>
                          </Link>
                        );
                      })}
                    </div>

                    {/* Right — Plan A Visit feature card */}
                    <div className="p-5 flex flex-col">
                      <div className="text-[9px] uppercase tracking-[0.3em] text-electric font-bold mb-3">First Time?</div>
                      <Link
                        to="/contact"
                        role="menuitem"
                        data-testid="nav-dropdown-about-plan-a-visit"
                        className="group flex-1 flex flex-col rounded-2xl bg-gradient-to-br from-electric/20 via-electric/5 to-[#0a0a0a] border border-electric/30 p-5 hover:border-electric transition"
                      >
                        <Zap className="w-8 h-8 text-electric mb-3" aria-hidden="true" />
                        <div className="font-display font-black text-xl tracking-tight text-white mb-2">Plan A Visit</div>
                        <p className="text-xs text-zinc-300 leading-relaxed mb-auto">
                          Get directions, service times, and what to expect.
                        </p>
                        <span className="inline-flex items-center gap-1 text-electric font-bold text-xs mt-4 group-hover:gap-2 transition-all">
                          Get In Touch <ArrowRight className="w-3 h-3" />
                        </span>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* MEMBER SERVICES — MEGA MENU */}
          <div
            className="relative"
            onMouseEnter={() => handleEnter("member-services")}
            onMouseLeave={handleLeave}
          >
            <button
              type="button"
              onClick={() => setActiveDropdown(activeDropdown === "member-services" ? null : "member-services")}
              aria-expanded={activeDropdown === "member-services"}
              aria-haspopup="true"
              data-testid="nav-dropdown-member-services"
              className={`inline-flex items-center gap-1 px-4 py-2 text-sm font-medium tracking-wide rounded-md transition-colors ${
                matchesAny(MEMBER_MATCH) || activeDropdown === "member-services" ? "text-electric" : "text-zinc-300 hover:text-white"
              }`}
            >
              Member Services
              <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${activeDropdown === "member-services" ? "rotate-180" : ""}`} aria-hidden="true" />
            </button>
            {activeDropdown === "member-services" && (
              <div
                role="menu"
                data-testid="nav-dropdown-panel-member-services"
                className="absolute left-1/2 -translate-x-1/2 top-full pt-3"
                onMouseEnter={() => handleEnter("member-services")}
                onMouseLeave={handleLeave}
              >
                <div className="bg-[#050505] border border-white/10 rounded-3xl shadow-2xl shadow-black/80 overflow-hidden ring-1 ring-electric/15 w-[720px]">
                  <div className="grid grid-cols-3 gap-0">
                    {/* Left column — 4 service tiles */}
                    <div className="col-span-2 p-4 grid grid-cols-2 gap-2 border-r border-white/5">
                      {MEMBER_SERVICES_ITEMS.map((sub) => {
                        const subActive = location.pathname === sub.to || location.pathname.startsWith(sub.to + "/");
                        return (
                          <Link
                            key={sub.to}
                            to={sub.to}
                            role="menuitem"
                            data-testid={`nav-dropdown-member-services-${sub.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className={`group relative overflow-hidden rounded-2xl border transition-all ${
                              subActive ? "border-electric bg-electric/5" : "border-white/5 hover:border-electric/40 bg-[#0a0a0a]"
                            }`}
                          >
                            <div className="relative h-28 overflow-hidden">
                              <img
                                src={sub.image}
                                alt=""
                                aria-hidden="true"
                                className={`absolute inset-0 w-full h-full object-cover ${sub.topAlign ? "object-top" : ""} opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-all duration-500`}
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-[#080808] via-[#080808]/40 to-transparent" />
                            </div>
                            <div className="relative -mt-6 p-4 pt-0">
                              <div className={`text-sm font-display font-bold tracking-tight mb-0.5 ${subActive ? "text-electric" : "text-white group-hover:text-electric"} transition-colors`}>
                                {sub.label}
                              </div>
                              <div className="text-[11px] text-zinc-500 leading-snug">{sub.description}</div>
                            </div>
                          </Link>
                        );
                      })}
                    </div>

                    {/* Right column — Member Login feature card */}
                    <div className="p-5 flex flex-col">
                      <div className="text-[9px] uppercase tracking-[0.3em] text-electric font-bold mb-3">Members</div>
                      {user && user.role === "member" ? (
                        <Link
                          to="/portal"
                          role="menuitem"
                          data-testid="nav-dropdown-member-services-portal"
                          className="group flex-1 flex flex-col rounded-2xl bg-gradient-to-br from-electric/20 via-electric/5 to-[#0a0a0a] border border-electric/30 p-5 hover:border-electric transition"
                        >
                          <Zap className="w-8 h-8 text-electric mb-3" aria-hidden="true" />
                          <div className="font-display font-black text-xl tracking-tight text-white mb-2">My Portal</div>
                          <p className="text-xs text-zinc-300 leading-relaxed mb-auto">
                            Jump into your groups, posts, and events.
                          </p>
                          <span className="inline-flex items-center gap-1 text-electric font-bold text-xs mt-4 group-hover:gap-2 transition-all">
                            Open Portal <ArrowRight className="w-3 h-3" />
                          </span>
                        </Link>
                      ) : (
                        <>
                          <Link
                            to="/login"
                            role="menuitem"
                            data-testid="nav-dropdown-member-services-member-login"
                            className="group flex flex-col rounded-2xl bg-gradient-to-br from-electric/20 via-electric/5 to-[#0a0a0a] border border-electric/30 p-5 hover:border-electric transition mb-2"
                          >
                            <Zap className="w-7 h-7 text-electric mb-3" aria-hidden="true" />
                            <div className="font-display font-black text-lg tracking-tight text-white mb-1">Member Login</div>
                            <p className="text-[11px] text-zinc-300 leading-relaxed">
                              Sign In To Your Portal
                            </p>
                            <span className="inline-flex items-center gap-1 text-electric font-bold text-xs mt-3 group-hover:gap-2 transition-all">
                              Sign In <ArrowRight className="w-3 h-3" />
                            </span>
                          </Link>
                          <Link
                            to="/signup"
                            role="menuitem"
                            data-testid="nav-dropdown-member-services-create-account"
                            className="group rounded-2xl border border-white/10 p-4 hover:border-electric/40 hover:bg-white/5 transition flex items-start gap-3"
                          >
                            <Zap className="w-4 h-4 text-electric mt-0.5 flex-shrink-0" aria-hidden="true" />
                            <div>
                              <div className="text-sm font-bold text-white group-hover:text-electric transition-colors">Create Account</div>
                              <div className="text-[11px] text-zinc-500 mt-0.5">Join The Push Family</div>
                            </div>
                          </Link>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <NavLink
            to="/giving"
            data-testid="nav-link-give"
            className={({ isActive }) =>
              `px-4 py-2 text-sm font-medium tracking-wide rounded-md transition-colors ${
                isActive ? "text-electric" : "text-zinc-300 hover:text-white"
              }`
            }
          >
            Give
          </NavLink>
          <NavLink
            to="/contact"
            data-testid="nav-link-contact"
            className={({ isActive }) =>
              `px-4 py-2 text-sm font-medium tracking-wide rounded-md transition-colors ${
                isActive ? "text-electric" : "text-zinc-300 hover:text-white"
              }`
            }
          >
            Contact
          </NavLink>
        </nav>

        {/* RIGHT CLUSTER */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {user && user.role === "admin" ? (
            <>
              <Link
                to="/admin"
                className="hidden xl:inline-flex items-center gap-1.5 text-sm font-bold text-electric hover:text-white transition px-3 py-2"
                data-testid="nav-admin-link"
              >
                <Zap className="w-4 h-4" />
                Admin
              </Link>
              <button onClick={handleLogout} className="hidden xl:inline-flex text-sm text-zinc-400 hover:text-white transition" data-testid="nav-logout-btn">
                Logout
              </button>
            </>
          ) : user && user.role === "member" ? (
            <>
              <Link
                to="/portal"
                className="hidden xl:inline-flex items-center gap-1.5 bg-electric text-black px-4 py-2 rounded-full font-bold text-xs uppercase tracking-wider hover:bg-[#4ade80] hover:-translate-y-0.5 transition-all duration-300 whitespace-nowrap"
                data-testid="nav-portal-link"
              >
                <Zap className="w-3.5 h-3.5" />
                My Portal
              </Link>
              <button onClick={handleLogout} className="hidden xl:inline-flex text-sm text-zinc-400 hover:text-white transition" data-testid="nav-logout-btn">
                Logout
              </button>
            </>
          ) : (
            <Link
              to="/contact"
              className="hidden xl:inline-flex items-center gap-1.5 bg-electric text-black px-4 py-2 rounded-full font-bold text-xs uppercase tracking-wider hover:bg-[#4ade80] hover:-translate-y-0.5 transition-all duration-300 whitespace-nowrap"
              data-testid="nav-cta-visit"
            >
              Plan A Visit
            </Link>
          )}

          <button
            onClick={() => setOpen(!open)}
            className="lg:hidden p-2 text-white"
            aria-label="Toggle menu"
            aria-expanded={open}
            data-testid="nav-mobile-toggle"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* MOBILE NAV */}
      {open && (
        <div className="lg:hidden border-t border-white/5 bg-black/95 backdrop-blur-xl" data-testid="nav-mobile-menu">
          <nav className="px-4 py-4 flex flex-col gap-1 max-h-[80vh] overflow-y-auto" aria-label="Mobile">
            <NavLink to="/" end onClick={() => setOpen(false)} data-testid="nav-mobile-link-home"
              className={({ isActive }) => `px-4 py-3 text-base font-medium rounded-md ${isActive ? "text-electric bg-electric/10" : "text-zinc-300"}`}>
              Home
            </NavLink>

            {/* About expandable */}
            <MobileGroup
              keyName="about"
              label="About"
              items={ABOUT_ITEMS}
              isActive={matchesAny(ABOUT_MATCH)}
              expanded={!!mobileExpanded.about}
              onToggle={() => setMobileExpanded({ ...mobileExpanded, about: !mobileExpanded.about })}
              onClose={() => setOpen(false)}
            />

            {/* Member Services expandable */}
            <MobileGroup
              keyName="member-services"
              label="Member Services"
              items={[
                ...MEMBER_SERVICES_ITEMS.map((i) => ({ to: i.to, label: i.label, description: i.description })),
                ...(user && user.role === "member"
                  ? [{ to: "/portal", label: "My Portal", description: "Open My Portal" }]
                  : [
                      { to: "/login", label: "Member Login", description: "Sign In To Your Portal" },
                      { to: "/signup", label: "Create Account", description: "Join The Push Family" },
                    ]),
              ]}
              isActive={matchesAny(MEMBER_MATCH)}
              expanded={!!mobileExpanded["member-services"]}
              onToggle={() => setMobileExpanded({ ...mobileExpanded, "member-services": !mobileExpanded["member-services"] })}
              onClose={() => setOpen(false)}
            />

            <NavLink to="/giving" onClick={() => setOpen(false)} data-testid="nav-mobile-link-give"
              className={({ isActive }) => `px-4 py-3 text-base font-medium rounded-md ${isActive ? "text-electric bg-electric/10" : "text-zinc-300"}`}>
              Give
            </NavLink>
            <NavLink to="/contact" onClick={() => setOpen(false)} data-testid="nav-mobile-link-contact"
              className={({ isActive }) => `px-4 py-3 text-base font-medium rounded-md ${isActive ? "text-electric bg-electric/10" : "text-zinc-300"}`}>
              Contact
            </NavLink>

            <div className="border-t border-white/5 mt-3 pt-3">
              {user && user.role === "admin" ? (
                <Link to="/admin" onClick={() => setOpen(false)} className="block px-4 py-3 text-base font-bold text-electric" data-testid="nav-mobile-admin">Admin Dashboard</Link>
              ) : null}
            </div>

            <div className="flex items-center justify-center gap-3 pt-3 border-t border-white/5 mt-2">
              <a href="https://www.facebook.com/share/1By8zRYFPB/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center text-zinc-400 hover:text-electric hover:border-electric transition">
                <Facebook className="w-4 h-4" />
              </a>
              <a href="https://www.instagram.com/pushcenterva" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center text-zinc-400 hover:text-electric hover:border-electric transition">
                <Instagram className="w-4 h-4" />
              </a>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}

function MobileGroup({ keyName, label, items, isActive, expanded, onToggle, onClose }) {
  return (
    <div className="rounded-md overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        data-testid={`nav-mobile-dropdown-${keyName}`}
        className={`w-full flex items-center justify-between px-4 py-3 text-base font-medium ${
          isActive ? "text-electric bg-electric/10" : "text-zinc-300"
        }`}
      >
        {label}
        <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? "rotate-180" : ""}`} aria-hidden="true" />
      </button>
      {expanded && (
        <div className="pl-3 pb-2 space-y-0.5 border-l border-electric/20 ml-4">
          {items.map((sub) => (
            <Link
              key={sub.to}
              to={sub.to}
              onClick={onClose}
              data-testid={`nav-mobile-dropdown-${keyName}-${sub.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="block px-4 py-2.5 rounded-md text-sm text-zinc-300 hover:text-electric hover:bg-white/5"
            >
              <div className="font-medium">{sub.label}</div>
              {sub.description && <div className="text-[10px] text-zinc-500">{sub.description}</div>}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
